/**
 * HreflangTags — emits <link rel="alternate" hreflang="..."> tags for the
 * current route based on /app/frontend/src/i18nConfig.js.
 *
 * Pairs with RouteHead.js (which owns canonical + og:url). Mounted once
 * inside <BrowserRouter>.
 *
 * • Today, only English is shipped → emits `hreflang="en"` + `hreflang="x-default"`.
 * • When a new locale is added to LOCALES in i18nConfig.js, this component
 *   automatically emits the additional tags. No code changes needed.
 *
 * The SSR layer in setupProxy.js also emits these tags into the static HTML
 * so first-crawl Googlebot sees them before React mounts.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { buildHreflangAlternates } from '../../i18nConfig';

const TAG_MARKER = 'data-hreflang-tag';

const HreflangTags = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      // 1. Remove ALL hreflang alternates currently in <head> — including
      //    SSR-injected ones from setupProxy.js. We become the single owner
      //    of these tags as soon as React mounts, preventing duplication.
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

      // 2. Inject fresh tags for this route
      const alternates = buildHreflangAlternates(location.pathname);
      for (const alt of alternates) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', alt.hreflang);
        link.setAttribute('href', alt.href);
        link.setAttribute(TAG_MARKER, 'true');
        document.head.appendChild(link);
      }
    } catch {
      /* defensive — never crash the app over SEO */
    }
  }, [location.pathname]);

  return null;
};

export default HreflangTags;
